import {
  MetricRegistry,
  videoLabel,
  type ReportMetricId,
  type TrendResponse,
} from "@meta-video/contracts";
import { LineChart, type LineChartSeries } from "@mantine/charts";
import { Button, Group, Stack, Text } from "@mantine/core";
import { useId, useMemo, useState } from "react";

import { useProductAnalytics } from "../../shared/analytics/useProductAnalytics.js";
import type { DotItemDotProps } from "recharts";

import { formatMetricValue } from "../report/format.js";

type SegmentStyle = "low" | "maturing" | "solid";

export interface TrendChartModel {
  readonly data: readonly Readonly<Record<string, null | number | string>>[];
  readonly isolatedPoints: ReadonlySet<string>;
  readonly logicalSeries: readonly Readonly<{
    color: string;
    id: string;
    keys: readonly string[];
    label: string;
  }>[];
  readonly series: readonly LineChartSeries[];
}

const colors = [
  "#1f77b4", "#ff7f0e", "#2ca02c", "#d62728", "#9467bd",
  "#8c564b", "#e377c2", "#7f7f7f", "#9c9d12", "#17becf",
  "#393b79", "#637939", "#8c6d31", "#843c39", "#7b4173",
  "#3182bd", "#31a354", "#756bb1", "#636363", "#e6550d",
] as const;

export function buildTrendChartModel(
  trend: TrendResponse,
): TrendChartModel {
  const byDay = new Map<string, Record<string, null | number | string>>();
  for (const point of trend.points) byDay.set(point.day, { day: point.day });
  const dayEpochs = new Map<string, number>();
  const dayEpoch = (day: string): number => {
    const cached = dayEpochs.get(day);
    if (cached !== undefined) return cached;
    const parsed = Date.parse(`${day}T00:00:00.000Z`);
    dayEpochs.set(day, parsed);
    return parsed;
  };
  const areConsecutiveDays = (left: string, right: string): boolean =>
    dayEpoch(right) - dayEpoch(left) === 86_400_000;
  const resolution = new Map(
    trend.resolution.map(({ belowThreshold, day }) => [day, belowThreshold]),
  );
  const groups = groupPoints(trend);
  const outputSeries: LineChartSeries[] = [];
  const isolatedPoints = new Set<string>();
  const logicalSeries: { color: string; id: string; keys: string[]; label: string }[] = [];

  groups.forEach((group, index) => {
    const derived = group.identity === null
      ? null
      : videoLabel({
          adCount: group.identity.adCount,
          brandId: group.identity.brandId,
          displayAdName: group.identity.displayAdName,
          lengthSeconds: group.identity.lengthSeconds,
          videoId: group.identity.videoId,
        });
    const label = derived === null
      ? "All videos"
      : `${derived.primary} — ${derived.secondary}`;
    const color = colors[index % colors.length]!;
    const keys = {
      low: `series_${index}__low`,
      maturing: `series_${index}__maturing`,
      solid: `series_${index}__solid`,
    } as const;
    const used = new Set<SegmentStyle>();
    const styles = group.points.map((point): SegmentStyle =>
      point.maturing
        ? "maturing"
        : resolution.get(point.day) === true ? "low" : "solid",
    );

    group.points.forEach((point, pointIndex) => {
      if (point.value === null) return;
      const ownStyle = styles[pointIndex]!;
      byDay.get(point.day)![keys[ownStyle]] = point.value;
      used.add(ownStyle);
      const previous = group.points[pointIndex - 1];
      const next = group.points[pointIndex + 1];
      const connectsLeft = previous !== undefined
        && previous.value !== null
        && areConsecutiveDays(previous.day, point.day);
      const connectsRight = next !== undefined
        && next.value !== null
        && areConsecutiveDays(point.day, next.day);
      if (!connectsLeft && !connectsRight) {
        isolatedPoints.add(isolatedPointKey(keys[ownStyle], point.day));
      }
    });
    for (let pointIndex = 1; pointIndex < group.points.length; pointIndex += 1) {
      const previous = group.points[pointIndex - 1]!;
      const current = group.points[pointIndex]!;
      if (
        previous.value === null
        || current.value === null
        || !areConsecutiveDays(previous.day, current.day)
      ) continue;
      const segmentStyle = segmentRisk(styles[pointIndex - 1]!, styles[pointIndex]!);
      byDay.get(previous.day)![keys[segmentStyle]] = previous.value;
      byDay.get(current.day)![keys[segmentStyle]] = current.value;
      used.add(segmentStyle);
    }

    (["solid", "low", "maturing"] as const).forEach((style) => {
      if (!used.has(style)) return;
      outputSeries.push({
        color,
        label: style === "solid" ? label : `${label} (${style === "low" ? "limited creative coverage" : "maturing"})`,
        name: keys[style],
        ...(style === "low" ? { strokeDasharray: "6 4" } : {}),
        ...(style === "maturing" ? { strokeDasharray: "3 4" } : {}),
      });
    });
    logicalSeries.push({
      color,
      id: group.identity === null
        ? "all-videos"
        : `${group.identity.brandId}\0${group.identity.videoId}`,
      keys: [...used].map((style) => keys[style]),
      label,
    });
  });

  for (const day of byDay.values()) {
    for (const series of outputSeries) {
      if (!(series.name in day)) day[series.name] = null;
    }
  }

  return {
    data: [...byDay.values()].sort((left, right) => String(left.day).localeCompare(String(right.day))),
    isolatedPoints,
    logicalSeries,
    series: outputSeries,
  };
}

function isolatedPointKey(seriesName: string, day: string): string {
  return `${seriesName}\0${day}`;
}

function groupPoints(trend: TrendResponse) {
  if (trend.series.length === 0) {
    return [{
      identity: null,
      points: [...trend.points].sort((left, right) => left.day.localeCompare(right.day)),
    }];
  }
  const pointsByIdentity = new Map<string, (typeof trend.points)[number][]>();
  for (const point of trend.points) {
    if (point.brandId === null || point.videoId === null) continue;
    const key = `${point.brandId}\0${point.videoId}`;
    const points = pointsByIdentity.get(key);
    if (points === undefined) pointsByIdentity.set(key, [point]);
    else points.push(point);
  }
  return trend.series.map((identity) => ({
    identity,
    points: [...(pointsByIdentity.get(`${identity.brandId}\0${identity.videoId}`) ?? [])]
      .sort((left, right) => left.day.localeCompare(right.day)),
  }));
}

function segmentRisk(left: SegmentStyle, right: SegmentStyle): SegmentStyle {
  if (left === "maturing" || right === "maturing") return "maturing";
  if (left === "low" || right === "low") return "low";
  return "solid";
}

export function TrendChart({
  defaultExpanded = false,
  metric,
  surface = "detail",
  trend,
}: {
  readonly defaultExpanded?: boolean;
  readonly metric: ReportMetricId;
  readonly surface?: "comparison" | "detail" | "report";
  readonly trend: TrendResponse;
}) {
  const analytics = useProductAnalytics();
  const model = useMemo(() => buildTrendChartModel(trend), [trend]);
  const [expanded, setExpanded] = useState(defaultExpanded);
  const chartId = useId();
  const headingId = useId();
  const metricLabel = MetricRegistry[metric].label;

  return (
    <section
      aria-labelledby={headingId}
      className="trend-card"
      data-expanded={expanded ? "true" : "false"}
    >
      <Stack gap={expanded ? "sm" : 0}>
        <Group justify="space-between" wrap="nowrap">
          <h2 id={headingId}>Daily {metricLabel}</h2>
          <Button
            aria-controls={chartId}
            aria-expanded={expanded}
            aria-label={`${expanded ? "Collapse" : "Expand"} daily ${metricLabel} trend`}
            onClick={() => {
              const next = !expanded;
              analytics.reportInteraction({
                action: "changed",
                feature: "trend",
                state: next ? "enabled" : "disabled",
                surface,
              });
              setExpanded(next);
            }}
            size="compact-xs"
            variant="subtle"
          >
            {expanded ? "Collapse" : "Expand"}
          </Button>
        </Group>
        {model.data.length === 0 ? (
          <Text c="dimmed">No daily trend matched these filters.</Text>
        ) : (
          <>
            <LineChart
              accessibilityLayer
              aria-label={`Daily ${metricLabel} chart`}
              connectNulls={false}
              curveType="linear"
              data={[...model.data]}
              dataKey="day"
              gridAxis={expanded ? "xy" : "none"}
              h={expanded ? 320 : 52}
              id={chartId}
              series={[...model.series]}
              lineProps={(series) => ({
                dot: isolatedDot(series.name, String(series.color), model.isolatedPoints),
              })}
              valueFormatter={(value) =>
                formatMetricValue(metric, value, trend.currency, { mixedCurrencyLabel: false })
                ?? "No data"}
              withDots={false}
              withLegend={false}
              withXAxis={expanded}
              withYAxis={expanded}
            />
            <Text c="dimmed" className="trend-card__trust" size="xs">
              Gaps mean no daily value. Dashed segments mark maturing attribution or limited creative coverage.
            </Text>
            {expanded ? (
              <ul aria-label="Trend series" className="trend-legend">
                {model.logicalSeries.map(({ color, id, label }) => (
                  <li key={id} title={label}>
                    <span aria-hidden="true" className="trend-legend__swatch" style={{ backgroundColor: color }} />
                    <span className="trend-legend__label">{label}</span>
                  </li>
                ))}
              </ul>
            ) : null}
          </>
        )}
      </Stack>
    </section>
  );
}

function isolatedDot(
  seriesName: string,
  color: string,
  isolatedPoints: ReadonlySet<string>,
) {
  return (props: DotItemDotProps) => {
    const payload: unknown = props.payload;
    if (!isDayPayload(payload) || !isolatedPoints.has(isolatedPointKey(seriesName, payload.day))) {
      return null;
    }
    return <circle aria-hidden="true" cx={props.cx} cy={props.cy} fill={color} r={3.5} />;
  };
}

function isDayPayload(value: unknown): value is { readonly day: string } {
  return typeof value === "object"
    && value !== null
    && "day" in value
    && typeof value.day === "string";
}
